import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import { AuthProvider } from './hooks/useAuth';
import { LanguageProvider } from './hooks/useLanguage.tsx';
import { ThemeProvider } from './hooks/useTheme.tsx';
import { ErrorBoundary } from './components/ErrorBoundary';
import { reportUnpaintedSharedTripLoad } from './lib/sharedTripPaintWatch';
import './index.css';

// Sentry — error tracking. Must run BEFORE createRoot so render errors
// are captured. DSN is public by design (Sentry ships it in client bundles).
// Org/project metadata, privacy posture, and the rationale for these settings
// live in docs/continuity/ASSETS.md and docs/continuity/SENTRY_CONFIG.md.
Sentry.init({
  dsn: 'https://de2ac7b4c121b87297bf36dd4542082a@o4511179885248512.ingest.de.sentry.io/4511236268556368',
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA ?? undefined,
  // Privacy: no automatic IP capture (GDPR-friendly default).
  sendDefaultPii: false,
  // Light performance sampling — 5%. Stays under free quota, useful for regressions.
  tracesSampleRate: 0.05,
  // Session Replay intentionally NOT enabled (privacy + quota).
  // Add later only if a hard-to-reproduce UI bug forces it.
});

// Before any page renders: a new shared-trip load overwrites the marker this reads.
reportUnpaintedSharedTripLoad();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
            <App />
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  </StrictMode>
);

/** Force the browser to re-check sw.js whenever the user returns to a tab.
 *  Browsers' default sw.js re-fetch interval is ~24h; on iOS standalone PWAs
 *  it only fires on cold start. Calling registration.update() on
 *  visibilitychange shrinks the staleness window from "could be days" to
 *  "next time the user looks at the tab" — critical for installed PWAs
 *  that get foregrounded but never killed. */
if ('serviceWorker' in navigator) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) reg.update().catch(() => { /* network blip — try again next focus */ });
    }).catch(() => { /* getRegistration failed — fine */ });
  });
}
